import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  ShieldCheck,
  Heart,
  Sparkles,
  Flower2,
  ArrowRight,
  Star,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import FloatingPetals from "../components/FloatingPetals";
import SectionTitle from "../components/SectionTitle";
import ServiceCard from "../components/ServiceCard";
import Reveal from "../components/Reveal";
import BookLink from "../components/BookLink";
import useSeo from "../hooks/useSeo";
import {
  business,
  images,
  services,
  trustBenefits,
  testimonials,
} from "../data/content";

const trustIcons = [ShieldCheck, Flower2, Sparkles, Heart];

export default function Home() {
  useSeo({
    title: `Soft Petal Electrolysis | Permanent Hair Removal in ${business.city}, ${business.state}`,
    description: `Experience personalized permanent hair removal at Soft Petal Electrolysis in ${business.city}, ${business.state}. Inclusive electrolysis care for all skin tones and hair types. Book your consultation today.`,
    path: "/",
    ogImage: images.ogShare,
  });

  return (
    <div>
      {/* HERO */}
      <section
        className="relative overflow-hidden isolate"
        data-testid="hero-section"
      >
        {/* Background image */}
        <div className="absolute inset-0 -z-10">
          <img
            src={images.hero}
            alt="Soft Petal Electrolysis client at sunset with pink lily petals floating in the breeze"
            className="w-full h-full object-cover slow-zoom"
          />
          {/* Left-to-right cream gradient overlay for text legibility on desktop */}
          <div
            aria-hidden="true"
            className="absolute inset-0 hidden lg:block"
            style={{
              background:
                "linear-gradient(90deg, hsl(var(--cream)) 0%, hsl(var(--cream) / 0.95) 22%, hsl(var(--cream) / 0.5) 42%, transparent 65%)",
            }}
          />
          {/* Mobile / tablet legibility overlay */}
          <div
            aria-hidden="true"
            className="absolute inset-0 lg:hidden"
            style={{
              background:
                "linear-gradient(180deg, hsl(var(--cream) / 0.85) 0%, hsl(var(--cream) / 0.55) 40%, hsl(var(--cream) / 0.35) 100%)",
            }}
          />
        </div>

        <FloatingPetals count={7} />

        <div className="max-w-7xl mx-auto px-6 sm:px-10 min-h-[78vh] lg:min-h-[86vh] flex items-center py-16 lg:py-20 relative">
          <div className="w-full lg:max-w-2xl fade-up">
            <h1 className="font-serif-display text-[2.65rem] sm:text-6xl lg:text-[4.4rem] leading-[1.02] tracking-tight text-charcoal">
              Radiate Confidence. <br />
              Reveal Your{" "}
              <span className="font-script text-petal text-[1.15em] leading-none petal-shimmer">
                Softest Self.
              </span>
            </h1>
            <p className="mt-7 font-body text-lg text-charcoal leading-relaxed max-w-xl">
              Gentle, precise, permanent electrolysis treatments designed to
              help you feel smooth, confident, and completely yourself.
            </p>
            <div className="mt-9 flex flex-wrap items-center gap-4">
              <BookLink  className="btn-petal" data-testid="hero-book-btn">
                Book Your Appointment
                <ArrowRight size={14} />
              </BookLink>
              <Link to="/services" className="btn-ghost-petal bg-warm-white/70 backdrop-blur-sm" data-testid="hero-services-btn">
                Explore Our Services
              </Link>
            </div>
            <p className="mt-8 font-body text-sm text-charcoal flex items-center gap-2">
              <Heart size={14} className="text-petal fill-petal" />
              Personalized care for every skin tone and hair type.
            </p>
          </div>
        </div>
      </section>

      {/* Trust benefits card row */}
      <section className="relative py-14 lg:py-16 bg-cream">
        <div className="max-w-6xl mx-auto px-6 sm:px-10 relative z-10 -mt-24 lg:-mt-20">
          <div className="bg-warm-white rounded-3xl shadow-soft border border-pale-rose/50 grid grid-cols-2 lg:grid-cols-4 divide-y lg:divide-y-0 lg:divide-x divide-pale-rose/50 overflow-hidden fade-up fade-up-delay-4">
            {trustBenefits.map((t, i) => {
              const Icon = trustIcons[i];
              return (
                <Reveal
                  key={t.title}
                  delay={i * 90}
                  className="p-6 sm:p-7 flex items-start gap-4 lift-on-hover"
                  data-testid={`trust-${i}`}
                >
                  <div className="w-11 h-11 rounded-full flex items-center justify-center bg-blush shrink-0">
                    <Icon size={18} className="text-petal" />
                  </div>
                  <div>
                    <h3 className="font-serif-display text-[1.15rem] leading-tight">
                      {t.title}
                    </h3>
                    <p className="font-body text-sm text-charcoal-soft mt-1.5 leading-snug">
                      {t.copy}
                    </p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* WELCOME */}
      <section className="py-16 lg:py-14 watercolor-cream relative" data-testid="welcome-section">
        <div className="max-w-6xl mx-auto px-6 sm:px-10 grid lg:grid-cols-12 gap-14 items-center">
          <Reveal className="lg:col-span-7">
            <SectionTitle
              eyebrow="Welcome"
              title="A Softer Approach to"
              scriptWord="Permanent Hair Removal"
              subtitle="At Soft Petal Electrolysis, every appointment is about more than removing unwanted hair. It is about helping you feel comfortable, understood, and confident in your skin. Each treatment is personalized to your needs and provided in a warm, judgment-free environment where your comfort always comes first."
              align="left"
            />
            <Link to="/about" className="btn-petal mt-9" data-testid="welcome-about-btn">
              Meet Your Electrologist
              <ArrowRight size={14} />
            </Link>
          </Reveal>
          <Reveal delay={140} className="lg:col-span-5">
            <div className="relative">
            <div className="rounded-3xl overflow-hidden bg-warm-white shadow-soft border border-pale-rose/40 p-3">
              <img
                src={images.inclusiveSkin}
                alt="Soft Petal Electrolysis: Smooth Confidence, Lasting Beauty campaign portrait"
                loading="lazy"
                width="1200"
                height="1200"
                className="w-full h-auto rounded-2xl block"
              />
            </div>
            <div className="absolute -bottom-6 -left-6 w-24 h-24 rounded-full bg-blush flex items-center justify-center shadow-soft float-gentle">
              <Flower2 className="text-petal" size={24} />
            </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* SERVICES PREVIEW */}
      <section className="py-16 lg:py-14 relative" data-testid="services-preview">
        <div className="max-w-7xl mx-auto px-6 sm:px-10">
          <SectionTitle
            eyebrow="Our Services"
            title="Gentle, Precise, and"
            scriptWord="Personalized"
            subtitle="Every session is designed around your comfort, your goals, and the natural rhythm of your hair-growth cycle."
          />
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-7">
            {services.map((s, i) => (
              <Reveal key={s.slug} delay={i * 110}>
                <ServiceCard service={s} testIdPrefix="home-service" />
              </Reveal>
            ))}
          </div>
          <div className="mt-14 text-center">
            <Link to="/services" className="btn-ghost-petal" data-testid="view-all-services">
              View All Services
              <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* FOUNDER STORY PREVIEW */}
      <section className="py-16 lg:py-14 bg-blush/50 relative overflow-hidden" data-testid="founder-preview">
        <FloatingPetals count={6} />
        <div className="max-w-6xl mx-auto px-6 sm:px-10 grid lg:grid-cols-12 gap-14 items-center relative">
          <Reveal className="lg:col-span-5">
            <div className="relative">
              <div className="rounded-[2.5rem] overflow-hidden aspect-[4/5] shadow-soft">
                <img
                  src={images.founder}
                  alt="The founder of Soft Petal Electrolysis at her electrolysis certification"
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-[1200ms] ease-out hover:scale-[1.04]"
                />
              </div>
              <div className="absolute -top-4 -right-4 w-24 h-24 rounded-full bg-warm-white flex items-center justify-center shadow-soft border border-pale-rose/60 float-gentle">
                <div className="text-center">
                  <div className="font-script text-petal text-2xl leading-none">with</div>
                  <div className="font-script text-petal text-2xl leading-none">love</div>
                </div>
              </div>
            </div>
          </Reveal>
          <Reveal delay={140} className="lg:col-span-7">
            <SectionTitle
              eyebrow="Our Story"
              title="Created From"
              scriptWord="Personal Experience"
              align="left"
            />
            <p className="mt-7 font-body text-lg text-charcoal-soft leading-relaxed max-w-2xl">
              Soft Petal Electrolysis was founded by someone who understands how
              deeply unwanted hair can affect confidence. After experiencing her
              own transformation through electrolysis, our founder was inspired
              to create the welcoming and inclusive space she once searched for
              herself.
            </p>
            <Link to="/about" className="btn-petal mt-9" data-testid="read-story-btn">
              Read Our Story
              <ArrowRight size={14} />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <TestimonialsCarousel />

      {/* BOOKING CTA */}
      <section className="relative overflow-hidden py-16 lg:py-14 bg-blush/60" data-testid="booking-cta">
        <FloatingPetals count={7} />
        <div className="max-w-3xl mx-auto px-6 text-center relative">
          <SectionTitle
            eyebrow="Book"
            title="Ready to Begin Your"
            scriptWord="Confidence Journey?"
            subtitle="Schedule your consultation and discover a treatment plan created especially for you."
          />
          <div className="mt-10">
            <BookLink  className="btn-petal" data-testid="cta-book-btn">
              Book Now
              <ArrowRight size={14} />
            </BookLink>
          </div>
        </div>
      </section>
    </div>
  );
}

function TestimonialsCarousel() {
  const [index, setIndex] = useState(0);
  const total = testimonials.length;
  const trackRef = useRef(null);
  const prev = () => setIndex((i) => (i - 1 + total) % total);
  const next = () => setIndex((i) => (i + 1) % total);

  return (
    <section className="py-16 lg:py-14 relative watercolor-cream" data-testid="testimonials-section">
      <div className="max-w-5xl mx-auto px-6 sm:px-10 text-center">
        <SectionTitle
          eyebrow="Kind Words"
          title="What Our"
          scriptWord="Clients Say"
          subtitle="Real people. Real results. Renewed confidence."
        />

        <div className="mt-14 relative">
          <div ref={trackRef} className="overflow-hidden">
            <div
              className="flex transition-transform duration-500 ease-out"
              style={{ transform: `translateX(-${index * 100}%)` }}
            >
              {testimonials.map((t, i) => (
                <div key={i} className="w-full shrink-0 px-4">
                  <div className="mx-auto max-w-2xl bg-warm-white rounded-3xl p-10 shadow-soft border border-pale-rose/40">
                    <div className="flex items-center justify-center gap-1 text-petal">
                      {Array.from({ length: t.rating }).map((_, r) => (
                        <Star
                          key={r}
                          size={18}
                          className="fill-petal sparkle"
                          strokeWidth={1}
                          style={{ animationDelay: `${r * 180}ms` }}
                        />
                      ))}
                    </div>
                    <p className="mt-5 font-review text-base sm:text-lg leading-relaxed text-charcoal">
                      &ldquo;{t.quote}&rdquo;
                    </p>
                    <div className="mt-6 font-review font-semibold text-sm text-charcoal">
                      {t.name}
                    </div>
                    <div className="font-review text-xs text-charcoal-soft mt-1">
                      {t.date} · {t.source} review
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-8 flex items-center justify-center gap-6">
            <button
              onClick={prev}
              aria-label="Previous testimonial"
              className="w-11 h-11 rounded-full border border-pale-rose/70 text-petal hover:bg-petal hover:text-white transition-colors flex items-center justify-center"
              data-testid="testimonial-prev"
            >
              <ChevronLeft size={18} />
            </button>
            <div className="flex gap-2">
              {testimonials.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setIndex(i)}
                  aria-label={`Go to testimonial ${i + 1}`}
                  className={`w-2.5 h-2.5 rounded-full transition-colors ${
                    i === index ? "bg-petal" : "bg-pale-rose"
                  }`}
                  data-testid={`testimonial-dot-${i}`}
                />
              ))}
            </div>
            <button
              onClick={next}
              aria-label="Next testimonial"
              className="w-11 h-11 rounded-full border border-pale-rose/70 text-petal hover:bg-petal hover:text-white transition-colors flex items-center justify-center"
              data-testid="testimonial-next"
            >
              <ChevronRight size={18} />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
